import React, { createContext, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export const CartContext = createContext();

// Read cart from localStorage (safely)
function readCartFromLocalStorage() {
  try {
    const raw = localStorage.getItem('cartItems');
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error('Failed to read cart from localStorage', e);
    return [];
  }
}

function normalizeItem(item) {
  if (!item) return item;
  return {
    id: item.id ?? item.productId ?? null,
    name: item.name ?? item.title ?? (item.snapshot && item.snapshot.name) ?? '',
    price: Number(item.price ?? (item.snapshot && item.snapshot.price) ?? 0),
    image: item.image ?? (item.snapshot && item.snapshot.image) ?? (item.snapshot && item.snapshot.thumbnail) ?? '',
    quantity: Number(item.quantity ?? 1),
    addedAt: item.addedAt ?? Date.now(),
    snapshot: item.snapshot ?? { ...(item || {}) },
  };
}

export const CartProvider = ({ children }) => {
  const queryClient = useQueryClient();

  const { data: cartItems = [] } = useQuery({
    queryKey: ['cart'],
    queryFn: readCartFromLocalStorage,
    staleTime: Infinity,
    cacheTime: Infinity,
    initialData: [],
  });

  // Utility to persist and update the cart in the React Query cache + localStorage
  const setCart = (updater) => {
    const current = queryClient.getQueryData(['cart']) || [];
    const next = typeof updater === 'function' ? updater(current) : updater;
    queryClient.setQueryData(['cart'], next);
    try {
      localStorage.setItem('cartItems', JSON.stringify(next));
    } catch (e) {
      console.error('Failed to persist cart to localStorage', e);
    }
  };

  const addToCart = (product, quantity = 1) => {
    const qty = Number(quantity) || 1;
    setCart((prevItems) => {
      const existing = (prevItems || []).find((it) => it.id === product.id);
      if (existing) {
        return (prevItems || []).map((it) =>
          it.id === product.id ? normalizeItem({ ...it, quantity: it.quantity + qty }) : it
        );
      }
      const newItem = normalizeItem({
        id: product.id,
        name: product.name || product.title,
        price: product.price ?? product.amount ?? 0,
        image: product.image ?? product.thumbnail ?? '',
        quantity: qty,
        addedAt: Date.now(),
        snapshot: { ...product },
      });
      return [...(prevItems || []), newItem];
    });
  };

  const updateQuantity = (productId, quantity) => {
    setCart((prevItems) =>
      (prevItems || [])
        .map((item) => (item.id === productId ? normalizeItem({ ...item, quantity: Math.max(0, quantity) }) : item))
        .filter((item) => item.quantity > 0)
    );
  };

  const removeFromCart = (productId) => {
    setCart((prevItems) => (prevItems || []).filter((item) => item.id !== productId));
  };

  const clearCart = () => setCart([]);

  const cartItemCount = useMemo(() => {
    return (cartItems || []).reduce((total, item) => total + item.quantity, 0);
  }, [cartItems]);

  const subtotal = useMemo(() => {
    return (cartItems || []).reduce((total, item) => total + Number(item.price) * item.quantity, 0);
  }, [cartItems]);

  const value = {
    cartItems,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    cartItemCount,
    subtotal,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};